import { Location } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, AuthService, BrewersUnitOfWork } from 'app/core/core.module';
import { CustomerType } from 'app/core/model/entity-model';
import { MixStrength } from 'app/core/model/mix-strength';
import { Product } from 'app/core/model/product';
import { ProductCategory } from 'app/core/model/product-category';
import { ProductCustomerTypeLink } from 'app/core/model/product-customer-type-link';
import { ProductLocation } from 'app/core/model/product-location';
import { ProductMaterial } from 'app/core/model/product-material';
import { ProductUnitOfMeasure } from 'app/core/model/product-unit-of-measure';
import { alert, confirm } from 'devextreme/ui/dialog';
import { default as validationEngine } from 'devextreme/ui/validation_engine';

@Component({
    standalone: false,
    selector: 'app-product-detail',
    templateUrl: './product-detail.component.html',
    styleUrls: ['./product-detail.component.scss'],
})
export class ProductDetailComponent implements OnInit {
    uow = inject(BrewersUnitOfWork);
    route = inject(ActivatedRoute);
    router = inject(Router);
    location = inject(Location);
    appInfo = inject(AppInformationService);
    auth = inject(AuthService);

    ProductCategory = ProductCategory;

    key: number | string;
    product: Product;
    deliveryProducts: Product[];
    categories: ProductCategory[];
    uoms: ProductUnitOfMeasure[];
    locations: ProductLocation[];
    materials: ProductMaterial[];
    mixStrengths: MixStrength[];
    partNumbers: string[];

    contractorTypePrice: ProductCustomerTypeLink;
    contractorPrice: number;
    retailTypePrice: ProductCustomerTypeLink;
    retailPrice: number;

    canDeactivate() {
        return !this.hasChanges();
    }

    async ngOnInit() {
        this.appInfo.pleaseWaitShow();
        this.categories = await this.uow.productCategories.whereActive('displayOrder, name');
        this.uoms = await this.uow.productUnitsOfMeasure.whereActive('displayOrder, name');
        this.locations = await this.uow.productLocations.whereActive('displayOrder, name');
        this.materials = await this.uow.productMaterials.whereActive('displayOrder, name');
        this.mixStrengths = await this.uow.mixStrengths.whereActive('displayOrder, name');

        this.key = this.route.snapshot.params['id'];
        if (this.key === 'new') {
            this.product = this.uow.products.createEntity({
                categoryId: null,
                locationId: null,
                materialId: null,
                cost: null,
                basePrice: null,
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
            });
        } else {
            await this.getData();
        }
        this.partNumbers = await this.uow.getProductPartNumbers(this.product.id);
        await this.getCustomerTypePricing();
        this.appInfo.pleaseWaitHide();
    }

    hasChanges() {
        const customerTypePricingChanged =
            (this.contractorTypePrice && this.contractorTypePrice.price !== this.contractorPrice) || (this.retailTypePrice && this.retailTypePrice.price !== this.retailPrice);
        return this.uow.hasChanges() || customerTypePricingChanged;
    }

    async getData() {
        this.product = await this.uow.products.byId(this.key);
        this.deliveryProducts = await this.uow.getDeliveryProducts(Number(this.key));
    }

    async getCustomerTypePricing() {
        const customerTypes = await this.uow.getLatestCustomerTypePrices(this.product.id);
        const contractor = customerTypes.find((type) => type.customerTypeId === CustomerType.Types.Contractor);
        this.contractorTypePrice = contractor ?? this.createCustomerTypePrice(CustomerType.Types.Contractor, 0);
        this.contractorPrice = this.contractorTypePrice.price;
        const retail = customerTypes.find((type) => type.customerTypeId === CustomerType.Types.Retail);
        this.retailTypePrice = retail ?? this.createCustomerTypePrice(CustomerType.Types.Retail, 0);
        this.retailPrice = this.retailTypePrice.price;
    }

    createCustomerTypePrice(customerTypeId: number, price: number) {
        return this.uow.productCustomerTypeLinks.createEntity({
            productId: this.product.id,
            customerTypeId: customerTypeId,
            price: price,
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
        });
    }

    async replaceCustomerTypePrice(customerTypePrice: ProductCustomerTypeLink) {
        if (!customerTypePrice) return;

        const isContractor = customerTypePrice.customerTypeId === CustomerType.Types.Contractor;
        const price = isContractor ? this.contractorPrice : this.retailPrice;

        customerTypePrice.deleted = new Date();
        customerTypePrice.deletedBy = this.auth.currentUser.userName;

        const newCustomerTypePrice = this.createCustomerTypePrice(customerTypePrice.customerTypeId, price);
        if (isContractor) this.contractorTypePrice = newCustomerTypePrice;
        else this.retailTypePrice = newCustomerTypePrice;
    }

    categoryChanged = (e: any) => {
        if (e.value !== ProductCategory.Types.RMix) this.product.mixStrengthId = null;
    };

    async save() {
        if (!this.valid()) {
            await alert('Please fix errors before saving', this.appInfo.settings.company);
            return;
        }

        this.appInfo.pleaseWaitShow();
        if (this.contractorTypePrice.price !== this.contractorPrice) {
            if (this.contractorTypePrice.entityAspect.entityState.isAdded()) this.contractorTypePrice.price = this.contractorPrice;
            else this.replaceCustomerTypePrice(this.contractorTypePrice);
        }
        if (this.retailTypePrice.price !== this.retailPrice) {
            if (this.retailTypePrice.entityAspect.entityState.isAdded()) this.retailTypePrice.price = this.retailPrice;
            else this.replaceCustomerTypePrice(this.retailTypePrice);
        }
        await this.uow.commit();
        if (this.key === 'new') {
            this.key = this.product.id;
            this.router.navigate([`../${this.product.id}`], { relativeTo: this.route, replaceUrl: true });
        }
        this.appInfo.pleaseWaitHide();
    }

    async cancel() {
        const result = await confirm('Are you sure you want to cancel changes?', this.appInfo.settings.company);
        if (!result) return false;

        this.contractorPrice = this.contractorTypePrice.price;
        this.retailPrice = this.retailTypePrice.price;
        this.uow.rollback();
        if (!this.key || this.key === 'new') {
            this.close();
            return false;
        }
        return false;
    }

    close() {
        try {
            this.location.back();
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }

    valid() {
        const groupConfig = validationEngine.getGroupConfig('productValidationGroup');
        if (groupConfig) {
            const result = validationEngine.validateGroup('productValidationGroup');
            return result.isValid;
        } else return false;
    }

    isUnique = (e) => {
        return !this.partNumbers.some((p) => p?.toLowerCase().trim() === e.value?.toLowerCase().trim());
    };
}
